import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import HomeAddressEditor from "../components/driver/HomeAddressEditor";

type CurrentUserResponse = {
  ok?: boolean;
  user?: {
    id?: number | string;
    username?: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    email?: string;
  };
  error?: string;
};

type DriverRoleResponse = {
  ok?: boolean;
  data?: {
    id?: number | string;
    tarifa_id?: number | string | null;
    user_id?: number | string;
    vehiculo?: string;
    patente?: string;
    dni?: string | number;
    DNI?: string | number;
    cuil?: string | number;
    CUIL?: string | number;
    alias?: string;
    cbu?: string;
    CBU?: string;
    comision?: string | number;
    active?: boolean;
  };
  error?: string;
};

type DriverExtraResponse = {
  ok?: boolean;
  extra?: {
    DNI_frente?: string;
    DNI_dorso?: string;
    cedula_frente?: string;
    cedula_dorso?: string;
    registro_frente?: string;
    registro_dorso?: string;
    selfie?: string;
  };
  error?: string;
};

const pageStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "300px minmax(0, 1fr)",
  gap: "12px",
  alignItems: "start",
};

const cardStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "20px",
  background: "var(--surface)",
  boxShadow: "var(--shadow)",
  padding: "18px",
};

const avatarWrapStyle: React.CSSProperties = {
  width: "100%",
  aspectRatio: "1 / 1",
  borderRadius: "18px",
  overflow: "hidden",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const avatarTextStyle: React.CSSProperties = {
  fontSize: "14px",
  fontWeight: 800,
  color: "var(--text-secondary)",
};

const avatarImgStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: "17px",
  fontWeight: 800,
  color: "var(--text-primary)",
  marginBottom: "12px",
};

const fieldsGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "10px",
};

const fieldStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "14px",
  background: "var(--surface-alt)",
  padding: "10px 12px",
};

const fieldLabelStyle: React.CSSProperties = {
  fontSize: "10px",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--text-secondary)",
  marginBottom: "5px",
  fontWeight: 800,
};

const fieldValueStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-primary)",
  fontWeight: 700,
};

const docsGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "10px",
};

const docCardStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "14px",
  background: "var(--surface-alt)",
  padding: "10px",
};

const docPreviewWrapStyle: React.CSSProperties = {
  width: "100%",
  aspectRatio: "1.2 / 1",
  borderRadius: "12px",
  overflow: "hidden",
  background: "var(--surface)",
  border: "1px solid var(--border)",
  marginTop: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const docImgStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const emptyStateStyle: React.CSSProperties = {
  padding: "24px 16px",
  borderRadius: "14px",
  border: "1px dashed var(--border)",
  textAlign: "center",
  color: "var(--text-secondary)",
  fontSize: "13px",
};

const getValue = (value?: string | number | boolean | null) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Sí" : "No";
  return String(value);
};

const safeString = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const buildFullName = (
  firstName?: string,
  lastName?: string,
  username?: string | null
): string => {
  const fullName = [firstName || "", lastName || ""]
    .map((value) => value.trim())
    .filter(Boolean)
    .join(" ");

  return fullName || username || "Driver";
};

const DriverProfile = () => {
  const { token, userId, username, firstName, lastName } = useAuth();

  const [fullName, setFullName] = useState<string>(
    buildFullName(firstName || "", lastName || "", username)
  );
  const [phone, setPhone] = useState<string>("");
  const [driverData, setDriverData] = useState<DriverRoleResponse["data"] | null>(null);
  const [driverExtra, setDriverExtra] = useState<DriverExtraResponse["extra"] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const fetchProfile = async () => {
      if (!token || !userId) {
        setError("No se pudo identificar el driver autenticado.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const userResponse = await authFetch(`/v2/users/${userId}`, { token });
        const userPayload = (await userResponse
          .json()
          .catch(() => ({}))) as CurrentUserResponse;

        if (userResponse.ok && userPayload?.user) {
          setFullName(
            buildFullName(
              userPayload.user.first_name || "",
              userPayload.user.last_name || "",
              userPayload.user.username || username
            )
          );
          setPhone(safeString(userPayload.user.phone));
        }

        const roleResponse = await authFetch(`/v2/users/${userId}/role`, { token });
        const rolePayload = (await roleResponse
          .json()
          .catch(() => ({}))) as DriverRoleResponse;

        if (!roleResponse.ok || rolePayload?.ok === false || !rolePayload?.data) {
          throw new Error(rolePayload?.error || "No se pudo cargar driver_data.");
        }

        setDriverData(rolePayload.data);

        const driverId = rolePayload.data.id;

        if (driverId) {
          const extraResponse = await authFetch(`/v2/drivers/${driverId}/extra`, {
            token,
          });

          const extraPayload = (await extraResponse
            .json()
            .catch(() => ({}))) as DriverExtraResponse;

          if (extraResponse.ok && extraPayload?.extra) {
            setDriverExtra(extraPayload.extra);
          }
        }
      } catch (err) {
        console.error(err);
        setError(err instanceof Error ? err.message : "Error cargando perfil.");
        setDriverData(null);
        setDriverExtra(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [token, userId, username]);

  const selfie = driverExtra?.selfie || "";

  const docs = useMemo(
    () => [
      { key: "DNI_frente", label: "DNI frente", value: driverExtra?.DNI_frente },
      { key: "DNI_dorso", label: "DNI dorso", value: driverExtra?.DNI_dorso },
      { key: "cedula_frente", label: "Cédula frente", value: driverExtra?.cedula_frente },
      { key: "cedula_dorso", label: "Cédula dorso", value: driverExtra?.cedula_dorso },
      { key: "registro_frente", label: "Registro frente", value: driverExtra?.registro_frente },
      { key: "registro_dorso", label: "Registro dorso", value: driverExtra?.registro_dorso },
    ],
    [driverExtra]
  );

  if (loading) {
    return <div style={emptyStateStyle}>Cargando perfil del driver...</div>;
  }

  if (error) {
    return <div style={emptyStateStyle}>{error}</div>;
  }

  return (
    <div style={pageStyle}>
      <div style={gridStyle}>
        <div style={cardStyle}>
          <div style={avatarWrapStyle}>
            {selfie ? (
              <img src={selfie} alt="Selfie del driver" style={avatarImgStyle} />
            ) : (
              <div style={avatarTextStyle}>Sin selfie</div>
            )}
          </div>

          <div style={{ marginTop: "14px" }}>
            <div style={sectionTitleStyle}>{fullName}</div>

            <div style={fieldsGridStyle}>
              <div style={fieldStyle}>
                <div style={fieldLabelStyle}>Vehículo</div>
                <div style={fieldValueStyle}>{getValue(driverData?.vehiculo)}</div>
              </div>

              <div style={fieldStyle}>
                <div style={fieldLabelStyle}>Patente</div>
                <div style={fieldValueStyle}>{getValue(driverData?.patente)}</div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <div style={cardStyle}>
            <div style={sectionTitleStyle}>Datos personales</div>

            <div style={fieldsGridStyle}>
              <div style={fieldStyle}>
                <div style={fieldLabelStyle}>Nombre y apellido</div>
                <div style={fieldValueStyle}>{getValue(fullName)}</div>
              </div>

              <div style={fieldStyle}>
                <div style={fieldLabelStyle}>DNI</div>
                <div style={fieldValueStyle}>
                  {getValue(driverData?.DNI || driverData?.dni)}
                </div>
              </div>

              <div style={fieldStyle}>
                <div style={fieldLabelStyle}>CUIL</div>
                <div style={fieldValueStyle}>
                  {getValue(driverData?.CUIL || driverData?.cuil)}
                </div>
              </div>

              <div style={fieldStyle}>
                <div style={fieldLabelStyle}>Teléfono</div>
                <div style={fieldValueStyle}>{getValue(phone)}</div>
              </div>

              <div style={fieldStyle}>
                <div style={fieldLabelStyle}>Alias</div>
                <div style={fieldValueStyle}>{getValue(driverData?.alias)}</div>
              </div>

              <div style={fieldStyle}>
                <div style={fieldLabelStyle}>CBU</div>
                <div style={fieldValueStyle}>
                  {getValue(driverData?.CBU || driverData?.cbu)}
                </div>
              </div>
            </div>
          </div>

          <div style={cardStyle}>
            <div style={sectionTitleStyle}>Mi domicilio</div>
            <HomeAddressEditor token={token} path="/v2/driver-home/me" />
          </div>

          <div style={cardStyle}>
            <div style={sectionTitleStyle}>Documentación</div>

            <div style={docsGridStyle}>
              {docs.map((doc) => {
                const preview = safeString(doc.value);

                return (
                  <div key={doc.key} style={docCardStyle}>
                    <div style={fieldLabelStyle}>{doc.label}</div>
                    <div style={fieldValueStyle}>
                      {preview ? "Archivo cargado" : "Sin archivo"}
                    </div>

                    <div style={docPreviewWrapStyle}>
                      {preview ? (
                        <img src={preview} alt={doc.label} style={docImgStyle} />
                      ) : (
                        <span style={avatarTextStyle}>Sin preview</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverProfile;